import { useCallback, useEffect, useState } from "react";
import { useDotBotsAuth } from "@dotbots-boutique/auth-sdk/react";
import { useApi } from "../lib/api";
import { useI18n } from "../i18n/I18nProvider";
import { hasPermission } from "../lib/permissions";
import { confirmDialog, notifyDialog } from "../components/shared/modals";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL as string;

type ReportFormat = "csv" | "json" | "markdown" | "html" | "docx" | "pdf" | "pptx";

const FORMAT_EXT: Record<ReportFormat, string> = {
  csv: "csv", json: "json", markdown: "md", html: "html", docx: "docx", pdf: "pdf", pptx: "pptx",
};

interface ColumnDef {
  key: string;
  source?: string;
  aiInstruction?: string;
}

interface ReportPlan {
  filter: Record<string, unknown>;
  columns: ColumnDef[];
  explanation: string;
  summaryLanguage: string;
  originalQuery?: string;
}

interface JobStatus {
  status: "queued" | "running" | "succeeded" | "failed" | "cancelled";
  rowsProcessed: number;
  rowsTotal: number | null;
  filename: string | null;
  error: string | null;
  ready: boolean;
}

interface PlanResponse {
  plan: ReportPlan;
  matchCount: number;
  sample: Array<{
    ticketNumber: number;
    subject: string;
    contactEmail: string | null;
    status: string;
    createdAt: string;
  }>;
  estimatedAiCalls: number;
  cappedAt: number;
}

interface HistoryRow {
  id: string;
  status: string;
  format: string;
  rows_total: number | null;
  rows_processed: number;
  filename: string | null;
  error: string | null;
  original_query: string | null;
  explanation: string | null;
  size_bytes: number | null;
  created_at: string;
}

// AI-driven ad-hoc ticket export.
//
// Two-step UI so the operator sees exactly which tickets will be
// pulled — and how many extra AI calls it will cost — before they hit
// generate. Matches the two backend endpoints:
//   POST /api/reports/plan     → parse + preview
//   POST /api/reports/generate → materialise + stream file
export default function AiReports() {
  const { t, lang } = useI18n();
  const api = useApi();
  const { user, fetch: authFetch } = useDotBotsAuth();
  const roles = user?.roles ?? [];
  const canUse = hasPermission(roles, "ai.use");

  const [query, setQuery] = useState("");
  const [format, setFormat] = useState<ReportFormat>("csv");
  const [planning, setPlanning] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PlanResponse | null>(null);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Load the org's previously stored reports. Called on mount and
  // after every successful generate so the newest row appears at
  // the top of the list without a page refresh.
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get<{ data: HistoryRow[] }>("/api/reports/jobs");
      setHistory(res.data);
    } catch {
      // Non-fatal — the create flow still works without the list.
    } finally {
      setHistoryLoading(false);
    }
  }, [api]);

  useEffect(() => { if (canUse) void loadHistory(); }, [canUse, loadHistory]);

  // Fetches the finished bytes for one stored report and triggers a
  // browser save. Shared by the "just finished" download and the
  // history re-download button.
  const downloadJob = useCallback(async (jobId: string, fallbackExt: string, storedFilename: string | null) => {
    const url = `${BACKEND_URL.replace(/\/$/, "")}/api/reports/jobs/${jobId}/download`;
    const response = await authFetch(url, { method: "GET" });
    if (!response.ok) throw new Error(`API ${response.status}: ${await response.text().catch(() => "")}`);
    const disposition = response.headers.get("Content-Disposition") ?? "";
    const match = /filename="([^"]+)"/.exec(disposition);
    const filename = match?.[1] ?? storedFilename ?? `ticket-report.${fallbackExt}`;
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
  }, [authFetch]);

  const deleteJob = async (row: HistoryRow) => {
    const confirmed = await confirmDialog({
      title: t("reports.deleteConfirmTitle"),
      message: t("reports.deleteConfirmBody", { name: row.filename ?? row.original_query ?? row.id }),
      confirmLabel: t("common.delete"),
      cancelLabel: t("common.cancel"),
    });
    if (!confirmed) return;
    try {
      await api.del(`/api/reports/jobs/${row.id}`);
      setHistory((prev) => prev.filter((r) => r.id !== row.id));
    } catch (err) {
      void notifyDialog({ message: (err as Error).message });
    }
  };

  const downloadFromHistory = async (row: HistoryRow) => {
    try {
      await downloadJob(row.id, row.format, row.filename);
    } catch (err) {
      void notifyDialog({ message: (err as Error).message });
    }
  };

  const runPlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setPlanning(true);
    setError(null);
    setPreview(null);
    try {
      const res = await api.post<PlanResponse>("/api/reports/plan", {
        query: query.trim(),
        language: lang,
      });
      setPreview(res);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setPlanning(false);
    }
  };

  const runGenerate = async () => {
    if (!preview) return;
    setGenerating(true);
    setError(null);
    setProgress(null);
    try {
      // Start the background job. The server returns immediately
      // with an id; we then poll /status until it's ready. Guard
      // against an empty or unexpected body so a stale backend
      // (endpoint not yet deployed) surfaces a clear message
      // instead of "Cannot read properties of undefined".
      const startRes = await api.post<{ jobId?: string } | null>("/api/reports/jobs", {
        plan: preview.plan,
        format,
      });
      const jobId = startRes && typeof startRes === "object" ? startRes.jobId : undefined;
      if (!jobId) {
        throw new Error(t("reports.jobStartFailed"));
      }

      // Poll every 1.5s. Runs until the job is in a terminal state.
      // Empty/undefined responses (mid-deploy, transient 5xx that
      // still returns 200) are tolerated for one tick before giving
      // up, so a single flaky poll doesn't kill the whole run.
      let finalStatus: JobStatus | null = null;
      let missTicks = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 1500));
        const status = await api.get<JobStatus | null>(`/api/reports/jobs/${jobId}/status`);
        if (!status || typeof status.status !== "string") {
          if (++missTicks >= 3) throw new Error(t("reports.jobStatusUnreadable"));
          continue;
        }
        missTicks = 0;
        setProgress({ done: status.rowsProcessed ?? 0, total: status.rowsTotal });
        if (status.status === "succeeded" || status.status === "failed" || status.status === "cancelled") {
          finalStatus = status;
          break;
        }
      }

      // finalStatus is set by every reachable break above, but keep
      // an explicit guard so the compiler stays happy and a future
      // refactor can't silently reintroduce the undefined-status
      // crash the user hit.
      if (!finalStatus) throw new Error(t("reports.jobStatusUnreadable"));
      if (finalStatus.status === "failed") throw new Error(finalStatus.error ?? "Job failed");
      if (finalStatus.status === "cancelled") {
        setError(t("reports.cancelled"));
        return;
      }

      await downloadJob(jobId, FORMAT_EXT[format], finalStatus.filename);
      // Newest report just landed — pull the updated history so it
      // shows up in the list below without a manual refresh.
      await loadHistory();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setGenerating(false);
      setProgress(null);
    }
  };

  if (!canUse) {
    return (
      <div className="page">
        <h2>{t("reports.noAccessTitle")}</h2>
        <p>{t("reports.noAccessBody")}</p>
      </div>
    );
  }

  return (
    <div className="page">
      <h2>{t("reports.title")}</h2>
      <p style={{ color: "var(--text-muted)", maxWidth: 720 }}>{t("reports.intro")}</p>

      <form className="card" onSubmit={runPlan} style={{ marginTop: 16, maxWidth: 900 }}>
        <div className="field">
          <label>{t("reports.queryLabel")}</label>
          <textarea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            rows={4}
            placeholder={t("reports.queryPlaceholder")}
            required
            minLength={3}
          />
          <small style={{ color: "var(--text-muted)", display: "block", marginTop: 4 }}>
            {t("reports.queryHelp")}
          </small>
        </div>

        <div className="field">
          <label>{t("reports.formatLabel")}</label>
          <select value={format} onChange={(e) => setFormat(e.target.value as ReportFormat)}>
            <option value="csv">CSV</option>
            <option value="json">JSON</option>
            <option value="markdown">Markdown</option>
            <option value="html">HTML</option>
            <option value="docx">Word (.docx)</option>
            <option value="pdf">PDF</option>
            <option value="pptx">PowerPoint (.pptx)</option>
          </select>
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="submit" className="primary" disabled={planning || !query.trim()}>
            {planning ? t("reports.planning") : t("reports.analyseButton")}
          </button>
        </div>
      </form>

      {error && (
        <div className="card" style={{ marginTop: 12, color: "var(--danger)", maxWidth: 900 }}>
          {error}
        </div>
      )}

      {preview && (
        <div className="card" style={{ marginTop: 16, maxWidth: 900 }}>
          <h3 style={{ marginTop: 0 }}>{t("reports.previewTitle")}</h3>
          <p><strong>{t("reports.explanationLabel")}:</strong> {preview.plan.explanation || "—"}</p>

          <ul style={{ listStyle: "none", padding: 0 }}>
            <li><strong>{t("reports.matchCount")}:</strong> {preview.matchCount}{preview.matchCount >= preview.cappedAt ? ` (${t("reports.cappedAt")} ${preview.cappedAt})` : ""}</li>
            <li><strong>{t("reports.estimatedCost")}:</strong> {preview.estimatedAiCalls} {t("reports.aiCalls")}</li>
          </ul>

          <h4>{t("reports.columns")}</h4>
          <ul style={{ paddingLeft: 20 }}>
            {preview.plan.columns.map((c) => (
              <li key={c.key} style={{ marginBottom: 4 }}>
                <code>{c.key}</code>
                {c.source
                  ? <> — <em style={{ color: "var(--text-muted)" }}>{t("reports.colField")}: {c.source}</em></>
                  : <> — <em style={{ color: "var(--text-muted)" }}>{t("reports.colAi")}:</em> {c.aiInstruction}</>}
              </li>
            ))}
          </ul>

          {preview.sample.length > 0 && (
            <>
              <h4>{t("reports.sampleTitle")}</h4>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", borderBottom: "1px solid var(--border)", padding: 6 }}>#</th>
                      <th style={{ textAlign: "left", borderBottom: "1px solid var(--border)", padding: 6 }}>{t("reports.colSubject")}</th>
                      <th style={{ textAlign: "left", borderBottom: "1px solid var(--border)", padding: 6 }}>{t("reports.colEmail")}</th>
                      <th style={{ textAlign: "left", borderBottom: "1px solid var(--border)", padding: 6 }}>{t("reports.colStatus")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sample.map((s) => (
                      <tr key={s.ticketNumber}>
                        <td style={{ padding: 6 }}>#{s.ticketNumber}</td>
                        <td style={{ padding: 6 }}>{s.subject}</td>
                        <td style={{ padding: 6 }}>{s.contactEmail ?? "—"}</td>
                        <td style={{ padding: 6 }}>{s.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
            <button type="button" onClick={() => setPreview(null)} disabled={generating}>
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className="primary"
              disabled={generating || preview.matchCount === 0}
              onClick={runGenerate}
            >
              {generating ? t("reports.generating") : t("reports.generateAndDownload")}
            </button>
          </div>
          {generating && progress && (
            <div style={{ marginTop: 12, color: "var(--text-muted)" }}>
              {t("reports.progress", {
                done: String(progress.done),
                total: progress.total != null ? String(progress.total) : "?",
              })}
            </div>
          )}
          {preview.plan.columns.some((c) => !!c.aiInstruction) && preview.matchCount > 20 && (
            <small style={{ display: "block", marginTop: 8, color: "var(--text-muted)" }}>
              {t("reports.largeJobWarning")}
            </small>
          )}
        </div>
      )}

      {/* Stored-reports history. Every successful generate writes a
          row here (via the report_jobs table); the operator can
          re-download the file or delete the whole entry. */}
      <div className="card" style={{ marginTop: 24, maxWidth: 900 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0 }}>{t("reports.historyTitle")}</h3>
          <button type="button" onClick={() => void loadHistory()} disabled={historyLoading}>
            {historyLoading ? t("common.loading") : t("common.refresh")}
          </button>
        </div>
        {history.length === 0 ? (
          <p style={{ color: "var(--text-muted)", marginTop: 8 }}>{t("reports.historyEmpty")}</p>
        ) : (
          <div style={{ overflowX: "auto", marginTop: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", borderBottom: "1px solid var(--border)", padding: 6 }}>{t("reports.histWhen")}</th>
                  <th style={{ textAlign: "left", borderBottom: "1px solid var(--border)", padding: 6 }}>{t("reports.histFormat")}</th>
                  <th style={{ textAlign: "left", borderBottom: "1px solid var(--border)", padding: 6 }}>{t("reports.histRows")}</th>
                  <th style={{ textAlign: "left", borderBottom: "1px solid var(--border)", padding: 6 }}>{t("reports.histQuery")}</th>
                  <th style={{ textAlign: "left", borderBottom: "1px solid var(--border)", padding: 6 }}>{t("reports.histStatus")}</th>
                  <th style={{ borderBottom: "1px solid var(--border)", padding: 6 }}></th>
                </tr>
              </thead>
              <tbody>
                {history.map((row) => {
                  const when = new Date(row.created_at).toLocaleString();
                  const queryPreview = (row.original_query ?? row.explanation ?? "").slice(0, 120);
                  const ready = row.status === "succeeded";
                  return (
                    <tr key={row.id}>
                      <td style={{ padding: 6, whiteSpace: "nowrap" }}>{when}</td>
                      <td style={{ padding: 6, textTransform: "uppercase" }}>{row.format}</td>
                      <td style={{ padding: 6 }}>{row.rows_total ?? row.rows_processed}</td>
                      <td style={{ padding: 6 }} title={row.original_query ?? undefined}>
                        {queryPreview || "—"}
                      </td>
                      <td style={{ padding: 6 }}>
                        {row.status}
                        {row.error && <div style={{ color: "var(--danger)", fontSize: 12 }}>{row.error}</div>}
                      </td>
                      <td style={{ padding: 6, whiteSpace: "nowrap", textAlign: "right" }}>
                        <button
                          type="button"
                          onClick={() => void downloadFromHistory(row)}
                          disabled={!ready}
                          style={{ marginRight: 6 }}
                        >
                          {t("reports.download")}
                        </button>
                        <button type="button" onClick={() => void deleteJob(row)}>
                          {t("common.delete")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
